/**
 * Cliente HTTP da edição auditável do laudo (E23.F8 — ADR-0025). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/runtime/laudo.py: // ux-neutrality:allow doc-comment
 * - GET  /casos/:id/laudo/preview        → documento completo read-only. // ux-neutrality:allow doc-comment
 * - POST /casos/:id/laudo/edit/propose   → proposta (não persiste). // ux-neutrality:allow doc-comment
 * - POST /casos/:id/laudo/edit/confirm   → aplica + grava trilha. // ux-neutrality:allow doc-comment
 * - GET  /casos/:id/laudo/edits          → trilha cronológica. // ux-neutrality:allow doc-comment
 */
import { type AxiosInstance } from 'axios';
import { classifyLaudoError, type VarianteConclusao } from '@/lib/laudo';

/** Campos de prosa que o assistente pode reescrever. */
export type ProposableField =
  | 'estrutura_geral'
  | 'morfologia'
  | 'conectividade'
  | 'inclinacao'
  | 'sequencia'
  | 'avaliacao_tecnica'
  | 'sintese'
  | 'observacao_adicional';

/** Todos os campos editáveis (prosa + listas) cobertos pela trilha. */
export type NarrativeFieldKey = ProposableField | 'convergencias' | 'divergencias';

export type EditSource = 'manual' | 'agent_proposed' | 'human_override';

export interface LaudoPreviewContestadaData {
  case_analysis_id: string;
  letra: string;
  label: string;
  narrative_present: boolean;
  approved: boolean;
  variante: VarianteConclusao | null;
  conclusao_texto: string | null;
  estrutura_geral: string | null;
  morfologia: string | null;
  conectividade: string | null;
  inclinacao: string | null;
  sequencia: string | null;
  convergencias: string[];
  divergencias: string[];
  avaliacao_tecnica: string | null;
  sintese: string | null;
  observacao_adicional: string | null;
}

export interface LaudoPreviewData {
  case_display_code: string;
  case_title: string;
  escritorio_nome: string;
  perito_titular: string | null;
  contestadas: LaudoPreviewContestadaData[];
}

export interface LaudoEditProposalData {
  target_field: ProposableField;
  proposed_value: string;
  rationale: string;
}

export interface LaudoEditRecordData {
  id: string;
  case_analysis_id: string;
  target_kind: 'narrative_field' | 'criterion_override';
  target_ref: string;
  old_value: string;
  new_value: string;
  source: EditSource;
  justification: string | null;
  meta: Record<string, unknown> | null;
  usuario_id: string | null;
  created_at: string;
}

export interface LaudoEditConfirmResponse {
  narrative: { case_analysis_id: string } & Record<string, unknown>;
  edit: LaudoEditRecordData;
}

export async function getLaudoPreview(
  client: AxiosInstance,
  caseId: string,
): Promise<LaudoPreviewData> {
  try {
    const { data } = await client.get<LaudoPreviewData>(`/casos/${caseId}/laudo/preview`);
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

export async function proposeLaudoEdit(
  client: AxiosInstance,
  caseId: string,
  caseAnalysisId: string,
  instruction: string,
): Promise<LaudoEditProposalData> {
  try {
    const { data } = await client.post<LaudoEditProposalData>(
      `/casos/${caseId}/laudo/edit/propose`,
      { case_analysis_id: caseAnalysisId, instruction },
      { timeout: 60_000 },
    );
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

export async function confirmLaudoEdit(
  client: AxiosInstance,
  caseId: string,
  params: {
    caseAnalysisId: string;
    targetField: NarrativeFieldKey;
    newValue: string;
    source: 'manual' | 'agent_proposed';
    justification?: string;
    /** E137.F2: o valor do campo que o editor leu; se mudou, o servidor recusa (409). */
    valorBase?: string;
  },
): Promise<LaudoEditConfirmResponse> {
  try {
    const { data } = await client.post<LaudoEditConfirmResponse>(
      `/casos/${caseId}/laudo/edit/confirm`,
      {
        case_analysis_id: params.caseAnalysisId,
        target_field: params.targetField,
        new_value: params.newValue,
        source: params.source,
        justification: params.justification ?? null,
        ...(params.valorBase === undefined ? {} : { valor_base: params.valorBase }),
      },
    );
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

export async function listLaudoEdits(
  client: AxiosInstance,
  caseId: string,
): Promise<LaudoEditRecordData[]> {
  try {
    const { data } = await client.get<LaudoEditRecordData[]>(`/casos/${caseId}/laudo/edits`);
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}
